/**
 * MUI theme — Ciments du Maroc identity, in light and dark.
 *
 * The 2020 rebrand pairs a deep institutional blue with the leaf green of
 * the ammonite mark. Primary drives navigation and actions; the green is
 * reserved for success and the accent line under KPIs. In dark mode the
 * blue lightens so it stays legible on dark surfaces while the sidebar
 * keeps the deep-blue identity.
 *
 * `getTokens(mode)` collects every non-palette literal (chart series
 * colors, the sidebar surfaces) so no component hardcodes a hex/rgba —
 * components read them via `useTheme().tokens`. `contrastThreshold: 4.5`
 * makes MUI pick AA-compliant text on every semantic color (StateChip,
 * filled alerts) in both modes.
 */
import { createTheme, type Theme } from "@mui/material/styles";

export type ThemeMode = "light" | "dark";

/** Design tokens outside the MUI palette slots, per mode. */
export type AppTokens = {
  chart: {
    /** Runs series / primary bars — CDM blue (lightened on dark). */
    runs: string;
    /** Uploads series — CDM leaf green. */
    uploads: string;
  };
  sidebar: {
    /** Drawer surface — deep CDM blue in both modes. */
    bg: string;
    text: string;
    textMuted: string;
    divider: string;
    selectedBg: string;
  };
  /** Dark ink readable on the CDM green (WCAG AA — white on #78be20 is 2.3:1). */
  inkOnGreen: string;
};

declare module "@mui/material/styles" {
  interface Theme {
    tokens: AppTokens;
  }
  interface ThemeOptions {
    tokens?: AppTokens;
  }
}

const INK_ON_GREEN = "#0d2b16";

export function getTokens(mode: ThemeMode): AppTokens {
  if (mode === "dark") {
    return {
      chart: { runs: "#6ea8dc", uploads: "#8fd044" },
      sidebar: {
        bg: "#0b1926",
        text: "rgba(255,255,255,0.82)",
        textMuted: "rgba(255,255,255,0.7)",
        divider: "rgba(255,255,255,0.15)",
        selectedBg: "rgba(255,255,255,0.14)",
      },
      inkOnGreen: INK_ON_GREEN,
    };
  }
  return {
    chart: { runs: "#00447c", uploads: "#78be20" },
    sidebar: {
      bg: "#00335d",
      text: "rgba(255,255,255,0.82)",
      textMuted: "rgba(255,255,255,0.7)",
      divider: "rgba(255,255,255,0.15)",
      selectedBg: "rgba(255,255,255,0.14)",
    },
    inkOnGreen: INK_ON_GREEN,
  };
}

export function buildTheme(mode: ThemeMode): Theme {
  const tokens = getTokens(mode);
  const dark = mode === "dark";
  return createTheme({
    tokens,
    palette: dark
      ? {
          mode: "dark",
          contrastThreshold: 4.5,
          primary: { main: "#7fb1e0", dark: "#4d84b8", light: "#a9cbea" },
          secondary: { main: "#8fd044", dark: "#78be20", contrastText: tokens.inkOnGreen },
          background: { default: "#0e1620", paper: "#151f2b" },
          success: { main: "#66bb6a" },
          warning: { main: "#ffb74d" },
          error: { main: "#ef5350" },
        }
      : {
          mode: "light",
          contrastThreshold: 4.5,
          primary: { main: "#00447c", dark: "#00335d", light: "#3369a1" },
          secondary: { main: "#78be20", dark: "#5a9410", contrastText: tokens.inkOnGreen },
          background: { default: "#f4f6f9", paper: "#ffffff" },
          success: { main: "#2e7d32" },
          warning: { main: "#b26a00" },
          error: { main: "#c62828" },
        },
    typography: {
      fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 700 },
      subtitle2: { color: dark ? "#9fb0c0" : "#5a6472" },
      button: { textTransform: "none", fontWeight: 600 },
    },
    shape: { borderRadius: 6 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiCard: {
        defaultProps: { variant: "outlined" },
      },
    },
  });
}
